import { useRef } from 'react';
import {
  Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, Stack,
  Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import type { City, Covering } from '../api/types';
import { MapView } from './MapView';
import { PlaceCoverageReport } from './PlaceCoverageReport';
import { STOP_COLOURS } from '../theme';

/*
 * One place, full width: where it is and what the saved record says about it.
 *
 * Opened from two directions, which is why the shell is its own component.
 * From a planned round's Coverage tab it is a stop, carrying its number and a
 * way back to it on the map. From the place panel it is a candidate somebody
 * is deciding about before anything is planned, and it carries a way back to
 * what they were reading.
 *
 * The record inside is PlaceCoverageReport in both cases: the same question,
 * answered once.
 */

export interface CoveragePlace {
  /** Distinguishes one place from the next, for resetting filters. */
  key: string;
  name: string;
  lat: number;
  lng: number;
  /** Empty for a dropped pin, which has no place to look up. */
  poiId: string;
  /** "S", "E", a stop number — only where the place sits in a round. */
  marker?: string;
  kind?: 'place' | 'point';
  /** Where it is in the round, for taking the map there. */
  index?: number;
}

export function PlaceCoverageWindow({
  place, coverings, later = [], city, basemapUrl,
  onClose, onBack, backLabel = 'Back', onShowOnMap, onShowDetails,
}: {
  place: CoveragePlace;
  coverings: Covering[];
  later?: Covering[];
  /** Omit and the window is the record alone: no map, full width. */
  city?: City | null;
  basemapUrl?: string | null;
  onClose: () => void;
  /** Returns to whatever opened this, instead of closing outright. */
  onBack?: () => void;
  backLabel?: string;
  /** Takes the round's own map to this stop, behind the window. */
  onShowOnMap?: (index: number) => void;
  /** Opens the place's full record — hours, address, what is there. */
  onShowDetails?: (poiId: string) => void;
}) {
  const scroller = useRef<HTMLDivElement | null>(null);
  const mapped = Boolean(city);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      /* Tall enough that the map and the record sit side by side without
         either being a letterbox, and still short enough to leave whatever
         opened it visible at the edges. */
      slotProps={{ paper: { sx: { height: { xs: '100%', md: '82vh' } } } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
          {/*
            The way back, where there is somewhere to go back TO.

            This window is opened over the place panel as often as from a
            round, and closing it there would throw away what somebody was
            reading rather than returning them to it.
          */}
          {onBack && (
            <Tooltip title={backLabel}>
              <IconButton size="small" onClick={onBack} aria-label={backLabel} sx={{ mt: 0.25 }}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {place.marker && (
            <Box
              sx={{
                width: 30,
                height: 30,
                flexShrink: 0,
                borderRadius: '50%',
                bgcolor: place.marker === 'E'
                  ? STOP_COLOURS.end
                  : place.marker === 'S' || place.marker === 'S/E'
                    ? STOP_COLOURS.start
                    : STOP_COLOURS.visit,
                color: '#fff',
                display: 'grid',
                placeItems: 'center',
                fontSize: place.marker.length > 2 ? 10 : 13,
                fontWeight: 700,
                mt: 0.25,
              }}
            >
              {place.marker}
            </Box>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h2" sx={{ lineHeight: 1.25 }}>{place.name}</Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap' }} useFlexGap>
              {place.kind === 'point' && (
                <Chip size="small" variant="outlined" label="pin" sx={{ height: 22, fontSize: 11 }} />
              )}
              {onShowDetails && place.poiId && (
                <Button
                  size="small"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  onClick={() => onShowDetails(place.poiId)}
                  sx={{ textTransform: 'none', py: 0, minHeight: 22 }}
                >
                  About this place
                </Button>
              )}
            </Stack>
          </Box>

          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} sx={{ height: '100%' }}>
          {/*
            Where it is.

            Its own map rather than a nudge to one behind: this window covers
            whatever opened it, and a place name means nothing to somebody who
            cannot see which part of the city it is in.
          */}
          {city && (
            <Box
              sx={{
                position: 'relative',
                width: { xs: '100%', md: '42%' },
                minHeight: { xs: 240, md: 'auto' },
                borderRight: { md: 1 },
                borderBottom: { xs: 1, md: 0 },
                borderColor: 'divider',
              }}
            >
              <MapView
                city={city}
                plan={null}
                {...(basemapUrl ? { basemapUrl } : {})}
                pins={[{
                  lat: place.lat,
                  lng: place.lng,
                  label: place.name,
                  role: 'visit' as const,
                  fixed: true,
                }]}
                /* Centred on the place at street level. MapView opens on the
                   whole city otherwise, which is right for a round and
                   useless for a point. */
                focusPoint={{ lat: place.lat, lng: place.lng, zoom: 15 }}
              />
              {onShowOnMap && place.index !== undefined && (
                <Tooltip title="Take the round’s own map to this stop">
                  <Button
                    size="small"
                    variant="contained"
                    color="inherit"
                    startIcon={<MyLocationIcon sx={{ fontSize: 16 }} />}
                    onClick={() => { onShowOnMap(place.index!); onClose(); }}
                    sx={{
                      position: 'absolute', bottom: 8, left: 8, zIndex: 4,
                      textTransform: 'none', bgcolor: 'background.paper', boxShadow: 3,
                    }}
                  >
                    Show in the round
                  </Button>
                </Tooltip>
              )}
            </Box>
          )}

          {/* The record, scrolling on its own so the map stays put. */}
          <Box
            ref={scroller}
            sx={{
              flex: 1,
              minWidth: 0,
              overflowY: 'auto',
              px: 2,
              pt: 1.5,
              pb: 2,
              ...(mapped ? {} : { maxWidth: 760, mx: 'auto', width: '100%' }),
            }}
          >
            <PlaceCoverageReport
              coverings={coverings}
              later={later}
              resetKey={place.key}
              scrollRoot={scroller}
            />
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
